"use client";

import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Shield, X } from "lucide-react";
import { useTranslations } from "next-intl";

import { useRecipeFrom } from "@/app/hooks/useRecipeOrigin";
import { useSession } from "@/app/hooks/useSession";
import { NomboxLogo } from "@/components/shared/NomboxLogo";
import { Link, usePathname } from "@/i18n/navigation";
import { cn } from "@/lib/utils";

import { NAV_DESTINATIONS } from "./destinations";
import { NavFooter } from "./NavFooter";
import { useNav } from "./NavProvider";

// Mobile-only slide-in drawer. Lives in [locale]/layout.tsx; visibility
// driven by NavProvider. Hidden on md+ (DesktopRail covers that breakpoint).
export function AppDrawer() {
  const { drawerOpen, closeDrawer } = useNav();
  const pathname = usePathname();
  const from = useRecipeFrom();
  const t = useTranslations("nav");
  const { isAuthenticated, data: sessionData } = useSession();
  const isAdmin = (sessionData?.user as any)?.role === "admin";

  return (
    <DialogPrimitive.Root
      open={drawerOpen}
      onOpenChange={(open) => {
        if (!open) closeDrawer();
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            "fixed inset-0 z-50 bg-black/60 backdrop-blur-sm md:hidden",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          )}
        />
        <DialogPrimitive.Content
          aria-label={t("primary")}
          className={cn(
            "fixed inset-y-0 start-0 z-50 flex w-[280px] max-w-[85vw] flex-col",
            "border-e border-border bg-surface-glass backdrop-blur-lg backdrop-saturate-150 pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] shadow-xl",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left",
            "duration-200 md:hidden"
          )}
        >
          <div className="flex items-center justify-between px-3 py-4">
            <Link
              href={isAuthenticated ? "/discover" : "/welcome"}
              onClick={closeDrawer}
              className="flex items-center gap-2"
            >
              <NomboxLogo className="text-foreground" />
              <span className="text-lg font-semibold">Nombox</span>
            </Link>
            <DialogPrimitive.Close
              aria-label={t("closeDrawer")}
              className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <X className="size-5" aria-hidden />
            </DialogPrimitive.Close>
          </div>
          <nav className="flex flex-1 flex-col gap-1 overflow-y-auto p-2">
            {NAV_DESTINATIONS.map((d) => {
              const Icon = d.icon;
              const active = isActive(pathname, d.href, from);
              return (
                <Link
                  key={d.id}
                  href={d.href}
                  prefetch={true}
                  onClick={closeDrawer}
                  className={cn(
                    "flex items-center gap-3 rounded-md px-3 py-3 text-base transition-colors",
                    active
                      ? "bg-muted text-foreground"
                      : "text-muted-foreground hover:bg-muted/60 hover:text-foreground"
                  )}
                  aria-current={active ? "page" : undefined}
                >
                  <Icon className="size-5" aria-hidden />
                  <span>{t(d.i18nKey)}</span>
                </Link>
              );
            })}
            {isAdmin && (
              <Link
                href="/admin"
                prefetch={true}
                onClick={closeDrawer}
                className={cn(
                  "flex items-center gap-3 rounded-md px-3 py-3 text-base transition-colors",
                  isActive(pathname, "/admin", from)
                    ? "bg-muted text-foreground"
                    : "text-muted-foreground hover:bg-muted/60 hover:text-foreground"
                )}
                aria-current={isActive(pathname, "/admin", from) ? "page" : undefined}
              >
                <Shield className="size-5" aria-hidden />
                <span>{t("admin")}</span>
              </Link>
            )}
          </nav>
          <NavFooter variant="drawer" onItemClick={closeDrawer} />
          <DialogPrimitive.Title className="sr-only">{t("primary")}</DialogPrimitive.Title>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

function isActive(pathname: string, href: string, from: string | null): boolean {
  if (href === "/") return pathname === "/";

  const isRecipeSub = /^\/recipes\//.test(pathname);
  if (isRecipeSub && from) {
    return href === `/${from}`;
  }

  return pathname === href || pathname.startsWith(href + "/");
}
